import { Injectable } from '@angular/core';
import { catchError, switchMap, map, tap, withLatestFrom } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { ProjectEntityListActions } from './project-entity-list.actions';
import { ProjectEntityService } from 'src/app/core/services/project.service';
import { Store } from '@ngrx/store';
import { selectQueryParams } from './project-entity-list.selectors';
import { FileTransferService } from 'src/app/core/services/file-transfer.service';
import { ProductTreeActionsService } from 'src/app/core/services/productTreeActions.service';
import { ProductTreeService } from 'src/app/core/services/productTree.service';
import { selectProductContext } from 'src/app/project/store/product-context/product-context.selector';

@Injectable()
export class ProjectEntityListEffects {
  constructor(
    private actions$: Actions,
    private projectService: ProjectEntityService,
    private productTreeService: ProductTreeService,
    private fileTransferService: FileTransferService,
    private productTreeActionsService: ProductTreeActionsService,
    private store: Store
  ) {}

  queryProjectEntityListStart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectEntityListActions.query_entity_list_start),
        withLatestFrom(this.store.select(selectQueryParams), this.store.select(selectProductContext)),
        switchMap(([_, queryParams, context]) => {
          return forkJoin([
            this.projectService.queryProjectEntities(queryParams.searchPhrase, queryParams.page, queryParams.pageSize),
            this.productTreeActionsService.queryProductTreeActions(null, context)
          ]).pipe(
            map(([projectList, rootActions]) => {
              return ProjectEntityListActions.query_entity_list_success({ projectList, rootActions });
            }),
            catchError(error => of(ProjectEntityListActions.query_entity_list_fail({ error: error })))
          );
        })
      ),
    { dispatch: true }
  );

  queryProjectEntityListFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectEntityListActions.query_entity_list_fail),
        tap(data => {
          throw data.error;
        })
      ),
    { dispatch: false }
  );

  deleteProjectEntityStart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectEntityListActions.delete_entity_start),
        switchMap(data =>
          this.productTreeService.delete(data.entityIdList).pipe(
            map(() => ProjectEntityListActions.delete_entity_success()),
            catchError(error => of(ProjectEntityListActions.delete_entity_fail({ error: error })))
          )
        )
      ),
    { dispatch: true }
  );

  copyProjectEntityStart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectEntityListActions.copy_entity_start),
        switchMap(data =>
          this.productTreeService.copy(data.id, null).pipe(
            map(() => ProjectEntityListActions.copy_entity_success()),
            catchError(error => of(ProjectEntityListActions.copy_entity_fail({ error: error })))
          )
        )
      ),
    { dispatch: true }
  );

  importXmlProjectEntityStart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProjectEntityListActions.import_xml_start),
        switchMap(data =>
          this.fileTransferService.importFromXml(data.file, null).pipe(
            map(() => ProjectEntityListActions.import_xml_success()),
            catchError(error => of(ProjectEntityListActions.import_xml_fail({ error: error })))
          )
        )
      ),
    { dispatch: true }
  );

  actionProjectEntitySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProjectEntityListActions.delete_entity_success,
          ProjectEntityListActions.copy_entity_success,
          ProjectEntityListActions.import_xml_success,
          ProjectEntityListActions.change_paging,
          ProjectEntityListActions.search
        ),
        map(() => ProjectEntityListActions.query_entity_list_start())
      ),
    { dispatch: true }
  );

  actionProjectEntityListFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProjectEntityListActions.delete_entity_fail,
          ProjectEntityListActions.copy_entity_fail,
          ProjectEntityListActions.import_xml_fail
        ),
        tap(data => {
          throw data.error;
        })
      ),
    { dispatch: false }
  );
}
